import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  LayoutDashboard,
  Building2,
  Users,
  Package,
  Briefcase,
  FileText,
  ShoppingCart,
  CheckSquare,
  Activity,
  Settings,
  LogOut,
  Sun,
  Moon,
  Menu,
  ChevronDown,
  User,
  Target,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Eye,
  X, ClipboardCheck, Search, Sun as SunRise, Sparkles } from 'lucide-react';
import Breadcrumbs from './Breadcrumbs';
import EmailVerificationNotice from './EmailVerificationNotice';
import BrandMark from './BrandMark';
import ShortcutSheet from './ShortcutSheet';
import CommandPalette from './CommandPalette';
import AssistantDrawer from '../assistant/AssistantDrawer';
import { useAssistant } from '../assistant/AssistantContext';
import { isMac } from '../keyboard/shortcuts';
import { useShortcut } from '../keyboard/ShortcutContext';
import './Layout.css';

const navItems = [
  { to: '/', icon: SunRise, label: 'Today', end: true },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/accounts', icon: Building2, label: 'Accounts', entity: 'accounts' },
  { to: '/contacts', icon: Users, label: 'Contacts', entity: 'contacts' },
  { to: '/opportunities', icon: Target, label: 'Opportunities', entity: 'opportunities' },
  { to: '/rep-firms', icon: Briefcase, label: 'Rep Firms', entity: 'rep_firms' },
  { to: '/products', icon: Package, label: 'Products', entity: 'products' },
  { to: '/quotes', icon: FileText, label: 'Quotes', entity: 'quotes' },
  { to: '/orders', icon: ShoppingCart, label: 'Orders', entity: 'orders' },
  { to: '/action-items', icon: CheckSquare, label: 'Action Items', entity: 'tasks' },
  { to: '/activities', icon: Activity, label: 'Activities', entity: 'activities' },
  { to: '/reports', icon: BarChart3, label: 'Reports', entity: 'reports' },
  // The weekly executive review: managers only (see canReview below).
  { to: '/executive-review', icon: ClipboardCheck, label: 'Executive Review', managersOnly: true },
];

function Layout({ children }) {
  const { user, logout, hasPermission, canDo, isModuleVisible, modulesLoaded, isImpersonating, originalUser, stopImpersonating } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const assistant = useAssistant();

  // Keyboard: g then a letter jumps to a section; "/" focuses the page's
  // search box; "?" (in ShortcutSheet) lists everything registered.
  const focusSearch = () => {
    const el = document.querySelector('.search-input input, input[type="search"]');
    if (el) el.focus();
  };
  useShortcut('mod+k', () => setPaletteOpen((o) => !o), { description: 'Search and commands', group: 'General', allowInInput: true, allowInModal: true });
  useShortcut('mod+/', () => assistant.toggle(), { description: 'Open or close the AI Assistant', group: 'General', allowInInput: true, allowInModal: true });
  useShortcut('g t', () => navigate('/'), { description: 'Go to Today', group: 'Navigate' });
  useShortcut('g d', () => navigate('/dashboard'), { description: 'Go to the dashboard', group: 'Navigate' });
  useShortcut('g a', () => navigate('/accounts'), { description: 'Go to accounts', group: 'Navigate' });
  useShortcut('g c', () => navigate('/contacts'), { description: 'Go to contacts', group: 'Navigate' });
  useShortcut('g o', () => navigate('/opportunities'), { description: 'Go to opportunities', group: 'Navigate' });
  useShortcut('g i', () => navigate('/action-items'), { description: 'Go to action items', group: 'Navigate' });
  useShortcut('g v', () => navigate('/activities'), { description: 'Go to activities', group: 'Navigate' });
  useShortcut('g r', () => navigate('/reports'), { description: 'Go to reports', group: 'Navigate' });
  useShortcut('/', focusSearch, { description: 'Focus the search box', group: 'General' });
  useShortcut('[', () => setSidebarCollapsed((c) => !c), { description: 'Collapse or expand the sidebar', group: 'General' });

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="layout">
      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="impersonation-banner">
          <div className="impersonation-banner-content">
            <Eye size={16} />
            <span>Viewing as <strong>{user?.name}</strong> ({user?.role_name})</span>
          </div>
          <button className="impersonation-exit-btn" onClick={stopImpersonating}>
            <X size={14} />
            Back to {originalUser?.name || 'Admin'}
          </button>
        </div>
      )}

      {/* Skip to main content link */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileMenuOpen(false)} role="presentation" />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <h1 className="sidebar-logo" title="ActiveOptics">
            <BrandMark size={32} />
            <span className={sidebarCollapsed ? 'sr-only' : 'sidebar-wordmark'}>ActiveOptics</span>
          </h1>
          {/* Sits on the sidebar's edge, half over the page, so it reads as the thing that moves the edge. */}
          <button
            type="button"
            className="collapse-toggle"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight size={16} strokeWidth={2.5} aria-hidden="true" /> : <ChevronLeft size={16} strokeWidth={2.5} aria-hidden="true" />}
          </button>
        </div>

        <nav className="sidebar-nav" role="navigation" aria-label="Main navigation">
          {!modulesLoaded ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="nav-skeleton-item" aria-hidden="true" />
            ))
          ) : (
            navItems.filter(item => {
              // Managers only: anyone who can see every account, or who manages settings.
              if (item.managersOnly) return user?.role_name === 'Admin' || hasPermission('view_all_accounts') || hasPermission('manage_settings');
              // Dashboard is always visible
              if (!item.entity) return true;
              // Check module visibility (from admin settings)
              if (!isModuleVisible(item.entity)) return false;
              // Check entity-level permissions
              return canDo(item.entity, 'view');
            }).map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                title={label}
                aria-label={label}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon size={20} aria-hidden="true" />
                {!sidebarCollapsed && <span>{label}</span>}
              </NavLink>
            ))
          )}
        </nav>
      </aside>

      {/* Main content */}
      <div className={`main-wrapper ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${assistant.open ? 'assistant-open' : ''}`} style={assistant.open ? { '--assistant-width': `${assistant.width}px` } : undefined}>
        {/* Top bar */}
        <header className="topbar" role="banner">
          <div className="topbar-left">
            <button className="btn-icon mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={mobileMenuOpen}>
              <Menu size={20} />
            </button>
            <Breadcrumbs />
          </div>

          <div className="topbar-right">
            <button
              type="button"
              className={`topbar-ask ${assistant.open ? 'is-open' : ''}`}
              onClick={() => assistant.toggle()}
              aria-label="Open the AI Assistant"
              aria-pressed={assistant.open}
              aria-keyshortcuts="Meta+/ Control+/"
              title={`AI Assistant (${isMac ? '⌘/' : 'Ctrl+/'})`}
            >
              <Sparkles size={15} aria-hidden="true" />
              <span>AI Assistant</span>
              <kbd>{isMac ? '⌘/' : 'Ctrl /'}</kbd>
            </button>
            <button
              type="button"
              className="topbar-search"
              onClick={() => setPaletteOpen(true)}
              aria-label="Search and commands"
              aria-keyshortcuts="Meta+K Control+K"
              title={`Search and commands (${isMac ? '⌘K' : 'Ctrl+K'})`}
            >
              <Search size={16} aria-hidden="true" />
              <span>Search…</span>
              <kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd>
            </button>
            <button className="btn-icon topbar-icon" onClick={toggleTheme} aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'} title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
            {hasPermission('manage_settings') && (
              <NavLink to="/settings" className="btn-icon topbar-icon" title="Settings" aria-label="Settings">
                <Settings size={20} />
              </NavLink>
            )}
            <div className="user-menu">
              <button
                className="user-menu-trigger"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                aria-label={`User menu for ${user?.name}`}
              >
                <div className="user-avatar" aria-hidden="true">
                  <User size={16} />
                </div>
                <span className="user-name">{user?.name}</span>
                <ChevronDown size={16} aria-hidden="true" />
              </button>

              {userMenuOpen && (
                <>
                  <div className="user-menu-overlay" onClick={() => setUserMenuOpen(false)} role="presentation" />
                  <div className="user-menu-dropdown" role="menu" aria-label="User menu">
                    <div className="user-menu-header">
                      <div className="user-menu-name">{user?.name}</div>
                      <div className="user-menu-email">{user?.email}</div>
                      <div className="user-menu-role">{user?.role_name}</div>
                    </div>
                    <EmailVerificationNotice compact />
                    <div className="user-menu-divider" role="separator" />
                    <button className="user-menu-item" onClick={handleLogout} role="menuitem">
                      <LogOut size={16} aria-hidden="true" />
                      <span>Log out</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="main-content" role="main">
          {children}
        </main>
      </div>
      <ShortcutSheet />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <AssistantDrawer />
    </div>
  );
}

export default Layout;
